/**
 * Per-tab record of what the signed-in account has done this session
 * (PLA-1110): accepted the AI Governance Policy, committed to goals, or
 * dismissed the goals reminder.
 *
 * sessionStorage is the right scope: it survives the MSAL redirect round-trip
 * and a same-tab refresh, and is gone when the tab closes, so the next login
 * prompts again. Each value is the MSAL homeAccountId, so switching accounts in
 * the same tab prompts again too. Sign-out clears everything explicitly.
 *
 * Every access is wrapped: storage can throw in private windows or when site
 * data is blocked, and a missing flag must only ever mean "prompt again".
 */

const KEYS = {
  aiGovernance: 'kai.loginAttestation',
  goalsCommitted: 'kai.goalsCommitment',
  goalsDismissed: 'kai.goalsReminderDismissed',
} as const;

type Flag = keyof typeof KEYS;

function has(flag: Flag, homeAccountId: string): boolean {
  try {
    return window.sessionStorage.getItem(KEYS[flag]) === homeAccountId;
  } catch {
    return false;
  }
}

function set(flag: Flag, homeAccountId: string): void {
  try {
    window.sessionStorage.setItem(KEYS[flag], homeAccountId);
  } catch {
    // Storage unavailable: the user will simply be asked again next load.
  }
}

/* AI Governance acceptance (blocking, at sign-in) ---------------------------- */

export const hasAttestedThisSession = (homeAccountId: string) => has('aiGovernance', homeAccountId);
export const markAttested = (homeAccountId: string) => set('aiGovernance', homeAccountId);

/* Goals commitment (non-blocking reminder) ---------------------------------- */

export const hasCommittedGoalsThisSession = (homeAccountId: string) =>
  has('goalsCommitted', homeAccountId);
export const markGoalsCommitted = (homeAccountId: string) => set('goalsCommitted', homeAccountId);

export const isGoalsReminderDismissed = (homeAccountId: string) =>
  has('goalsDismissed', homeAccountId);
export const dismissGoalsReminder = (homeAccountId: string) => set('goalsDismissed', homeAccountId);

/** Sign-out: the next sign-in in this tab starts from scratch. */
export function clearAttested(): void {
  for (const key of Object.values(KEYS)) {
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      // Nothing to clear.
    }
  }
}
